import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { ABI, ADDRESS, TOKENS } from './../../Contract';
import ImgUsdt from '../../assets/img/usdt.png';
import ImgBnb from '../../assets/img/bnb.png';
import WalletConnect from '../../Components/WalletConnect';
import SwappingCardRight from './SwappingCardRight';
import { useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import TokenDrawer from './TokenDrawer';
import { useTranslation } from '../../Context/LanguageContext';

const SwappingCard = (props) => {
  const { t } = useTranslation();
  const adminRef = useRef(null);
  const [contract, setContract] = useState(null);
  const [fromContract, setFromContract] = useState(null);
  const [toContract, setToContract] = useState(null);
  const [hvamUSD, setHvamUSD] = useState(0.53);
  const [fromBalance, setFromBalance] = useState(0);
  const [toBalance, setToBalance] = useState(0);
  const [txStatus, setTxStatus] = useState('');
  const [adminAddress, setAdminAddress] = useState('');
  const [isAdmin, setIsAdmin] = useState('');
  const [totalSupply, setTotalSupply] = useState(0);
  const [adminStatus, setAdminStatus] = useState('');

  const { show } = props;

  const [fromAmount, setFromAmount] = useState(0);
  const [toAmount, setToAmount] = useState(0);

  // Drawer State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState('from'); // 'from' or 'to'

  const { isConnected, address: account } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider('eip155');
  const [library, setLibrary] = useState(null);

  const fromTokens = [{ id: 'HVAM', tag: 'HVAM', img: ImgBnb, DECIMALS: 18 }];
  const [selectedFromToken, setSelectedFromToken] = useState(fromTokens[0]);

  const toTokens = [
    { id: 1, tag: 'USDT', img: ImgUsdt, DECIMALS: 18 },
    { id: 2, tag: 'BNB', img: ImgBnb, DECIMALS: 8 },
    { id: 3, tag: 'BTCB', img: ImgBnb, DECIMALS: 18 },
    { id: 4, tag: 'ETH', img: ImgBnb, DECIMALS: 18 },
    { id: 5, tag: 'BUSD', img: ImgBnb, DECIMALS: 18 },
  ];
  const [selectedToToken, setSelectedToToken] = useState(toTokens[0]);

  const openDrawer = (type) => {
    setDrawerType(type);
    setIsDrawerOpen(true);
  };

  const handleTokenSelect = (token) => {
    if (drawerType === 'from') {
      setSelectedFromToken(token);
    } else {
      setSelectedToToken(token);
      // Recalc logic if needed
      if (fromAmount > 0) calcToAmount(fromAmount);
    }
  };

  const resetState = () => {
    setFromAmount(0);
    setToAmount(0);
    getBalance(fromContract, setFromBalance);
    getBalance(toContract, setToBalance);
  };

  useEffect(() => {
    if (walletProvider) {
      const provider = new ethers.providers.Web3Provider(walletProvider);
      setLibrary(provider);
    } else {
      setLibrary(null);
    }
  }, [walletProvider]);

  const getContract = (address, ABI, provider) => {
    if (!provider) return null;
    const contract = new ethers.Contract(address, ABI, provider);

    return contract.connect(provider.getSigner());
  };

  const buyTokens = async () => {
    try {
      if (selectedToToken.id !== 2) {
        const allowance = await toContract.allowance(account, ADDRESS);

        setTxStatus('checking allowance');
        if (Number(allowance.toString()) < toAmount) {
          setTxStatus('Setting Allowance...');
          const atx = await toContract.approve(
            ADDRESS,
            ethers.utils
              .parseUnits('1000000', TOKENS[selectedToToken.tag].DECIMALS)
              .toString(),
          );
          setTxStatus('Processig Allowance...');
          await atx.wait();
          setTxStatus('Allowance Processed...');
        }
      }

      setTxStatus('Confirm Transaction on Wallet');
      const _value = selectedToToken.id !== 2 ? 0 : toAmount;
      const _fromAmount = ethers.utils
        .parseUnits(fromAmount, TOKENS[selectedFromToken.tag].DECIMALS)
        .toString();
      // const _fromAmount = fromAmount;
      const tx = await contract.buyTokens(_fromAmount, selectedToToken.id, {
        value: ethers.utils.parseEther(_value.toString()),
      });
      setTxStatus('Waiting For Confirmation');
      await tx.wait();
      setTxStatus('Transaction Completed');
      resetState();
    } catch (error) {
      setTxStatus(error?.error?.data?.message || 'Transaction Reverted');
    }
  };

  const getBalance = (contract, setter) => {
    if (contract && account && typeof contract.balanceOf === 'function') {
      contract.balanceOf(account).then((res) => {
        setter(Number(ethers.utils.formatEther(res.toString())).toFixed(8));
      });
    } else {
      if (selectedToToken.id === 2) {
        library?.getBalance(account).then((res) => {
          setter(Number(ethers.utils.formatEther(res.toString())).toFixed(12));
        });
      } else {
        setter(0);
      }
    }
  };

  useEffect(() => {
    if (isConnected && library) {
      setContract(getContract(ADDRESS, ABI, library));
    } else {
      setContract(null);
    }
  }, [library, account, isConnected]);

  useEffect(() => {
    if (contract) {
      Promise.all([
        contract
          .price()
          .then((res) => ethers.utils.formatUnits(res.toString(), 8)),
        contract.admin().then((res) => res.toString()),
        contract
          .totalTokenSold()
          .then((res) => ethers.utils.formatUnits(res.toString())),
      ])
        .then((vals) => {
          setHvamUSD(vals[0] || 0);
          setAdminAddress(vals[1] || '');
          setIsAdmin(vals[1] === account ? true : false);
          setTotalSupply(vals[2] || 0);
        })
        .catch((err) => console.log(err));
    }
  }, [contract, account]);

  useEffect(() => {
    if (isConnected && library) {
      const fromTokenAddress = TOKENS[selectedFromToken.tag].ADDRESS;
      const fromTokenABI = TOKENS[selectedFromToken.tag].ABI;
      const fContract = getContract(fromTokenAddress, fromTokenABI, library);
      setFromContract(fContract);
    } else {
      setFromContract(null);
    }
  }, [account, selectedFromToken, isConnected, library]);

  useEffect(() => {
    if (isConnected && selectedToToken.id !== 2 && contract && library) {
      const toTokenABI = TOKENS[selectedToToken.tag].ABI;

      contract.getTokensByType(selectedToToken.id).then((res) => {
        const tContract = getContract(res, toTokenABI, library);

        setToContract(tContract);
      });
    } else {
      setToContract(null);
    }
  }, [account, contract, selectedToToken, isConnected, library]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getBalance(fromContract, setFromBalance);
  }, [fromContract, account]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getBalance(toContract, setToBalance);
  }, [toContract, account]);

  const resetChildState = () => {
    typeof adminRef.current.resetState === 'function' &&
      adminRef.current.resetState();
  };

  const callPrice = async (amount) => {
    try {
      setAdminStatus('Please confirm transaction on wallet');

      const _amount = ethers.utils.parseUnits(amount, 8).toString();

      const tx = await contract.updatePrice(_amount);
      setAdminStatus('Waiting For Confirmation');

      await tx.wait();
      setAdminStatus('Pricing updated.');

      resetChildState();
    } catch (err) {
      setAdminStatus(err.message);
    }
  };

  const callToken = async (address) => {
    try {
      setAdminStatus('Please confirm transaction on wallet');

      const tx = await contract.setToken(address);
      setAdminStatus('Waiting For Confirmation');

      await tx.wait();
      setAdminStatus('Token updated.');

      resetChildState();
    } catch (error) {
      setAdminStatus(error.message);
    }
  };

  const callUpdateToken = async (address, receiveAddress) => {
    try {
      setAdminStatus('Please confirm transaction on wallet');

      const tx = await contract.inCaseTokensGetStuck(address, receiveAddress);
      setAdminStatus('Waiting For Confirmation');

      await tx.wait();
      setAdminStatus('Stucked token updated.');

      resetChildState();
    } catch (error) {
      setAdminStatus(error.message);
    }
  };

  function calcToAmount(amount) {
    amount = !amount ? '0' : amount;

    const _amount = ethers.utils
      .parseUnits(amount, TOKENS[selectedToToken.tag].DECIMALS)
      .toString();

    contract.amountNeedsToBePaid(_amount, selectedToToken.id).then((res) => {
      setToAmount(
        Number(
          ethers.utils.formatUnits(res.toString(), selectedToToken.DECIMALS),
        ).toFixed(8),
      );
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (contract && fromAmount > 0) {
      calcToAmount(fromAmount);
    } else {
      setToAmount(0);
    }
  }, [selectedFromToken, selectedToToken, contract, fromAmount]);

  return (
    <>
      <div className='w-100'>
        <div className='col-12'>
          <div className='card swap-card p-4'>
            {/* Header */}
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                <h5 className='mb-0 font-weight-bold' style={{ color: 'var(--text-primary)' }}>{t('swap')}</h5>
                <small className='text-muted'>{t('tradeTokensInstant')}</small>
              </div>
              <div className='d-flex align-items-center' style={{ color: 'var(--text-primary)' }}>
                <i className='far fa-chart-bar me-2 text-gold'></i>
                <small className='text-gold font-weight-bold'>
                  1 {selectedFromToken.tag || ''} = {hvamUSD} USD
                </small>
              </div>
            </div>

            {/* From Section */}
            <div className='swap-input-container mb-1'>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted text-xs font-weight-bold'>
                  {t('youPay')}
                </span>
                <span className='text-muted text-xs'>
                  {t('balance')}: <span style={{ color: 'var(--text-primary)' }}>{fromBalance}</span>
                </span>
              </div>
              <div className='d-flex align-items-center justify-content-between'>
                <input
                  type='number'
                  min='0'
                  step='0.0001'
                  className='swap-input'
                  placeholder='0'
                  value={fromAmount}
                  onChange={(e) => {
                    setFromAmount(e.target.value);
                    calcToAmount(e.target.value);
                  }}
                />

                {/* Drawer Trigger for FROM */}
                <div
                  className='token-selector-btn'
                  onClick={() => openDrawer('from')}
                >
                  <img
                    src={selectedFromToken.img}
                    alt={selectedFromToken.tag}
                  />
                  {selectedFromToken.tag}
                  <i className='fas fa-chevron-down ms-1 text-xs'></i>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className='swap-arrow-container'>
              <div className='swap-arrow-btn'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  style={{ color: '#D4AF37' }}
                >
                  <path
                    d='M11 5L6 9H16C18.7614 9 21 11.2386 21 14C21 16.7614 18.7614 19 16 19H3'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M3 19L8 23'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>

            {/* To Section */}
            <div className='swap-input-container mt-1 mb-4'>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted text-xs font-weight-bold'>
                  {t('youReceive')}
                </span>
                <span className='text-muted text-xs'>
                  {t('balance')}: <span style={{ color: 'var(--text-primary)' }}>{toBalance}</span>
                </span>
              </div>
              <div className='d-flex align-items-center justify-content-between'>
                <input
                  type='text'
                  className='swap-input'
                  placeholder='0'
                  readOnly
                  value={toAmount}
                />

                {/* Drawer Trigger for TO */}
                <div
                  className='token-selector-btn'
                  onClick={() => openDrawer('to')}
                >
                  <img src={selectedToToken.img} alt={selectedToToken.tag} />
                  {selectedToToken.tag}
                  <i className='fas fa-chevron-down ms-1 text-xs'></i>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className='text-center'>
              {isConnected ? (
                <button
                  type='button'
                  className='btn btn-gold w-100 py-3 text-lg shadow-lg'
                  onClick={() => buyTokens()}
                >
                  {t('swapNow')}
                </button>
              ) : (
                <WalletConnect className='btn btn-gold w-100 py-3 text-lg shadow-lg' />
              )}
              {txStatus && (
                <p className='mt-3 text-gold text-sm font-weight-bold text-uppercase letter-spacing-1'>
                  {txStatus}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Admin Section (Hidden unless props.show is true) */}
        <div className='col-12 col-md-4 mt-4 mt-md-0'>
          {show && (
            <SwappingCardRight
              isAdmin={isAdmin}
              adminAddress={adminAddress}
              hvamUSD={hvamUSD}
              fromToken={selectedFromToken.tag || ''}
              totalSupply={totalSupply}
              callPrice={callPrice}
              callToken={callToken}
              callUpdateToken={callUpdateToken}
              adminStatus={adminStatus}
              ref={adminRef}
            />
          )}
        </div>
      </div>

      {/* Token Drawer Component */}
      <TokenDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={drawerType === 'from' ? t('payWith') : t('receive')}
        tokens={drawerType === 'from' ? fromTokens : toTokens}
        onSelect={handleTokenSelect}
      />
    </>
  );
};

export default SwappingCard;
